import { isEmpty, isNil } from "lodash";
import { useMemo } from "react";
import CellRendererLoanComments from "../../../../../components/agGrid/cellRenderer/CellRendererLoanComments";
import CellRendererUserByEmail from "../../../../../components/agGrid/cellRenderer/CellRendererUserByEmail";
import OpenInNewTabButton from "../../../../../components/ui/buttons/OpenInNewTabButton";
import BorrowerSidepanelTable from "../../shared/BorrowerSidepanelTable";

const BorrowerLoansTable = ({ rowData }) => {
  const columnDefs = useMemo(
    () => [
      { hide: true, headerName: "_id", field: "_id" },
      {
        headerName: "Loan #",
        field: "loanNumber",
        minWidth: 90,
        maxWidth: 90,
        width: 90,
        suppressAutoSize: true,
        suppressHeaderFilterButton: true,
        flex: 0,
        getQuickFilterText: (params) =>
          params.value ? String(params.value) : "",
        filter: "agTextColumnFilter",
        cellRenderer: (params) => {
          if (params.node.group) return null;
          return (
            <OpenInNewTabButton
              linkIcon
              to={`/loan/${params?.data?.loanNumber}`}
            >
              {params?.data?.loanNumber}
            </OpenInNewTabButton>
          );
        },
        suppressFloatingFilterButton: true,
      },
      {
        field: "sync_loan_id",
        headerName: "External Loan ID",
        hide: true,
        width: 150,
        minWidth: 150,
        flex: 0,
      },
      { headerName: "Asset ID", field: "assetId", hide: true },
      {
        headerName: "Created",
        field: "createDateTime",
        type: "date",
        minWidth: 115,
        maxWidth: 115,
        width: 115,
        suppressHeaderFilterButton: true,
        suppressFloatingFilterButton: true,
        wrapHeaderText: true,
      },
      {
        headerName: "Updated",
        field: "updateDateTime",
        type: "date",
        minWidth: 115,
        maxWidth: 115,
        width: 115,
        hide: true,
        wrapHeaderText: true,
        suppressHeaderFilterButton: true,
      },
      {
        headerName: "Loan Name",
        field: "loanName",
        minWidth: 280,
        width: 360,
        // cellRenderer: (params) => {
        //   if (params.node.group) return null;
        //   return <CellRendererLoanName {...params} />;
        // },
        suppressSizeToFit: true,
        valueGetter: (params) =>
          isNil(params?.data?.loanName)
            ? `Loan #${params?.data?.loanNumber}`
            : params?.data?.loanName,
        suppressAutoSize: true,
        suppressHeaderFilterButton: true,
        autoHeight: true,
        suppressFloatingFilterButton: true,
        headerTooltip: "Go to loan",
        cellStyle: { cursor: "pointer" },
      },
      {
        headerName: "Loan Status",
        field: "loanStatus",
        minWidth: 140,
        width: 150,
        cellStyle: { cursor: "pointer", display: "flex", alignItems: "center" },
        flex: 0,
      },
      {
        headerName: "Sub Status",
        field: "subStatus",
        flex: 0,
        width: 200,
        minWidth: 150,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Comments",
        field: "comments",
        suppressAutoSize: true,
        suppressSizeToFit: true,
        suppressHeaderFilterButton: true,
        width: 600,
        minWidth: 350,
        flex: 0,
        autoHeight: true,
        wrapText: true,
        suppressFloatingFilterButton: true,
        cellStyle: {
          paddingTop: "14px",
          paddingBottom: "14px",
          paddingLeft: 0,
          cursor: "pointer",
        },
        cellRenderer: (params) => {
          if (params.node.group) return null;
          return <CellRendererLoanComments {...params} />;
        },
        cellClass: (params) => {
          if (isEmpty(params?.value)) return "ag-cell-vertical-center";
        },
        comparator: (valueA, valueB) => {
          const hasA = valueA && Array.isArray(valueA) && valueA.length > 0;
          const hasB = valueB && Array.isArray(valueB) && valueB.length > 0;
          if (!hasA && !hasB) return 0;
          if (!hasA) return -1;
          if (!hasB) return 1;
          return (
            new Date(valueA[0].commentDateTime).getTime() -
            new Date(valueB[0].commentDateTime).getTime()
          );
        },
      },
      {
        headerName: "Expected Closing Date",
        field: "expectedClosingDate",
        type: "date",
        flex: 0,
        width: 180,
      },
      {
        headerName: "Loan Product Type",
        field: "loanProductType",
        flex: 0,
        width: 200,
      },
      { headerName: "Loan Purpose", field: "loanPurpose", flex: 0, width: 200 },
      {
        headerName: "Salesperson",
        field: "salesperson",
        cellRenderer: (p) => {
          return <CellRendererUserByEmail {...p} stacked={true} />;
        },
      },
      { headerName: "Loan Closer", field: "loanCloser", hide: true },
      { headerName: "Processor", field: "processor", hide: true },
      { headerName: "Underwriter", field: "underwriter", hide: true },
      { field: "baseLoanToAsIsValue", type: "percent", hide: true },
      { field: "baseLoanToPurchasePrice", type: "percent", hide: true },
      {
        headerName: "Base Loan Amount",
        field: "baseLoanAmount",
        type: "dollar",
        hide: true,
      },
      {
        headerName: "Borrowers",
        field: "borrowers",
        hide: true,
        valueGetter: (params) => {
          const v = params.data?.borrowers;
          if (isEmpty(v) || isNil(v)) return;
          return v.map((item) =>
            isEmpty(item) || isNil(item?.firstName) || isNil(item?.lastName)
              ? "N/A"
              : `${item.firstName} ${item.lastName}`,
          );
        },
      },
      {
        headerName: "Closing Scheduled",
        field: "closingScheduled",
        type: "boolean",
        hide: true,
      },
      {
        headerName: "Closing Scheduled Date",
        field: "closingScheduledDate",
        type: "boolean",
        hide: true,
      },
      { field: "closingMonth", hide: true },
      { field: "closingQuarter", hide: true },
      { field: "closingWeek", hide: true },
      { field: "totalArv", type: "dollar", hide: true },
      { field: "totalAsIsValue", type: "dollar", hide: true },
      { field: "totalAssignmentFees", type: "dollar", hide: true },
      { field: "totalConstructionBudget", type: "dollar", hide: true },
      { field: "totalCostBasis", type: "dollar", hide: true },
      { field: "totalInterestReserves", type: "percent", hide: true },
    ],
    [],
  );

  return (
    <BorrowerSidepanelTable
      columnDefs={columnDefs}
      rowData={rowData}
      showEditIcon={false}
    />
  );
};

export default BorrowerLoansTable;
